import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import * as TokenSvc from 'services/token.svc'
import { Post } from 'services/http.svc'
import { initializeApp, getApps, getApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
export const ParamountAuthContext = createContext({})

const useAuthSessionStorage = () => {
  const [signInWith, _setSignInWith] = useState()
  const [authCode, _setAuthCode] = useState()
  const [accessToken, _setAccessToken] = useState()

  useEffect(() => {
    const siw = TokenSvc.getSignInWith()
    const ac = TokenSvc.getAuthCode()
    const at = TokenSvc.getAccessToken()
    _setSignInWith(siw)
    _setAuthCode(ac)
    _setAccessToken(at)
  }, [])

  const setSignInWith = useCallback(
    (value) => {
      if (value) {
        TokenSvc.setSignInWith(value)
      } else {
        TokenSvc.removeSignInWith()
      }
      _setSignInWith(value)
    },
    [_setSignInWith],
  )

  const setAccessToken = useCallback(
    (value) => {
      if (value) {
        TokenSvc.setAccessToken(value)
      } else {
        TokenSvc.removeAccessToken()
      }
      _setAccessToken(value)
    },
    [_setAccessToken],
  )

  const setAuthCode = useCallback(
    (value) => {
      if (value) {
        TokenSvc.setAuthCode(value)
      } else {
        TokenSvc.removeAuthCode()
      }
      _setAuthCode(value)
    },
    [_setAuthCode],
  )

  return {
    signInWith,
    setSignInWith,
    authCode,
    setAuthCode,
    accessToken,
    setAccessToken,
  }
}

const tokenGuts = (rawToken) => {
  if (rawToken) {
    const [, raw] = rawToken.split('.')
    if (raw) {
      return JSON.parse(atob(raw))
    }
  }
  return null
}

const ParamountAuthProvider = ({ children }) => {
  const { setSignInWith, setAuthCode, accessToken, setAccessToken } =
    useAuthSessionStorage()
  const [initialised, setInitialised] = useState(false)
  const [auth] = useState(() => {
    try {
      const decoded = atob(process.env.REACT_APP_FIREBASE_CONFIG_BASE64).replace(/'/g, '')
      const firebaseConfig = JSON.parse(decoded)
      
      const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp()
      const firebaseAuth = getAuth(app)
      
      return firebaseAuth
    } catch (err) {
      console.error('initFirebase FAILED:', err.message, err)
      return null
    }
  })

  // We keep initFirebase and useEffect for setting initialised 
  // but rely on lazy initialization to avoid component double rendering loop race
  const initFirebase = useCallback(() => {
     return auth
  }, [auth])

  useEffect(() => {
    if(auth) setInitialised(true)
  }, [auth, initFirebase])

  const fetchAccessToken = async (idToken, callback) => {
    try {
      const body = TokenSvc.getInviteCode('INVITE_CODE_TOKEN')
        ? { inviteCode: TokenSvc.getInviteCode('INVITE_CODE_TOKEN') }
        : null
      const response = await Post('auth/token', body, idToken)
      const { token } = response
      setAccessToken(token)
      if (callback) {
        callback()
      }
      return token
    } catch (err) {
      console.error(err)
    }
  }

  const isLoggedIn = useMemo(() => {
    if (accessToken) {
      const token = tokenGuts(accessToken)
      const exp = (token?.exp || 0) * 1000
      if (exp > new Date().getTime()) {
        return true
      }
    }
    return false
  }, [accessToken])

  const [userRoles, userCompanyId, companyStatus] = useMemo(() => {
    if (accessToken) {
      const token = tokenGuts(accessToken)
      return [token?.roles || [], token?.company, token?.companyStatus]
    }
    return []
  }, [accessToken])

  const logout = async () => {
    await auth?.signOut()
    setSignInWith(null)
    setAuthCode(null)
    setAccessToken(null)
  }

  const handleSignIn = async (signInWith, authCode, successCallback) => {
    setSignInWith(signInWith)
    setAuthCode(authCode)
    return fetchAccessToken(authCode, successCallback)
  }

  return (
    <ParamountAuthContext.Provider
      value={{
        handleSignIn,
        logout,
        isLoggedIn,
        userRoles,
        userCompanyId,
        companyStatus,
        initialised,
        setAccessToken,
        tokenGuts,
        fetchAccessToken,
        auth,
      }}
    >
      {children}
    </ParamountAuthContext.Provider>
  )
}

export default ParamountAuthProvider

export const useParamountAuth = () => {
  const context = useContext(ParamountAuthContext)
  if (!context) {
    throw new Error(
      'Cannot use useParamountAuth hook outside of ParamountAuthProvider',
    )
  }
  return context
}
